import { FormEvent, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { FormattedRpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";

type AddBackupServiceModalProps = {
    rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering;
    rpServiceConfigurationServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    addFallbackServiceOffering: (
        rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering,
        fallbackServiceOffering: FormattedRpServiceConfigurationServiceOffering,
        fallbackServiceOfferingDataSource: string
    ) => void;
    onModalClose: () => void;
};

type AddBackupServiceModalState = {
    selectedServiceOffering: SelectOption;
    dataSourceOptions: Array<SelectOption>;
    selectedDataSourceOption: SelectOption;
};

export const AddBackupServiceModal = (props: AddBackupServiceModalProps) => {
    const {
        rpServiceConfigurationServiceOffering,
        rpServiceConfigurationServiceOfferings,
        onModalClose,
        addFallbackServiceOffering
    } = props;

    const [state, setState] = useState<AddBackupServiceModalState>({
        selectedServiceOffering: null,
        dataSourceOptions: [],
        selectedDataSourceOption: null
    });

    const backupServiceOfferingOptions = useMemo(() => {
        return rpServiceConfigurationServiceOfferings.filter(so => so.serviceOfferingStatus === EntityStatusTypes.ACTIVE).map(so => ({ label: so.name, value: so.serviceOfferingID }));
    }, [rpServiceConfigurationServiceOfferings]);

    const onChangeBackupServiceSelect = (newValue: SelectOption) => {
        let dataSourceOptions: Array<SelectOption> = [];
        let selectedDataSourceOption: SelectOption = null;
        if (newValue) {
            const serviceOffering = rpServiceConfigurationServiceOfferings.find(so => so.serviceOfferingID === newValue.value);
            if (serviceOffering) {
                dataSourceOptions = serviceOffering.dataSources.map(ds => ({ label: ds.value, value: ds.value }));

                const defaultDataSource = serviceOffering.dataSources.find(ds => isStringTrue(ds.defaultFlag));
                if (defaultDataSource) {
                    selectedDataSourceOption = { label: defaultDataSource.value, value: defaultDataSource.value };
                }
            }
        }
        setState({
            selectedDataSourceOption: selectedDataSourceOption,
            dataSourceOptions: dataSourceOptions,
            selectedServiceOffering: newValue
        });
    };

    const onChangeDataSourceSelect = (newValue: SelectOption) => {
        setState({
            ...state,
            selectedDataSourceOption: newValue
        });
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        const fallbackServiceOffering = rpServiceConfigurationServiceOfferings.find(so => so.serviceOfferingID === state.selectedServiceOffering.value);
        addFallbackServiceOffering(rpServiceConfigurationServiceOffering, fallbackServiceOffering, state.selectedDataSourceOption?.value);
        onModalClose();
    };

    return (
        <DefaultModal
            automationId="add-backup-service-modal"
            ariaLabelledBy="add-backup-service-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={onSubmit}>
                <Modal.Header id={"add-backup-service-title"} closeButton>
                    <Modal.Title>{resources.modals.addBackupService.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SelectFormGroup
                        controlId={`backup-service-select`}
                        automationId={`backup-service-select`}
                        label={"Backup Service"}
                        ariaLabel={"Backup Service"}
                        disabled={false}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={false}
                        labelWidth={LabelWidthTypes.SMALL}
                        onChange={onChangeBackupServiceSelect}
                        options={backupServiceOfferingOptions}
                        selectedOption={state.selectedServiceOffering}
                    />
                    {state.selectedServiceOffering && state.dataSourceOptions.length > 1 && (
                        <SelectFormGroup
                            controlId={`backup-service-offering-data-source-select`}
                            automationId={`backup-service-offering-data-source-select`}
                            label={resources.pages.verificationServiceDetails.tabs.services.subTabs.common.inputs.dataSource.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.services.subTabs.common.inputs.dataSource.label}
                            disabled={false}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            labelWidth={LabelWidthTypes.SMALL}
                            onChange={onChangeDataSourceSelect}
                            options={state.dataSourceOptions}
                            selectedOption={state.selectedDataSourceOption}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-backup-service-cancel-button" variant="secondary" onClick={onModalClose}>
                        {resources.common.buttons.cancel.label}
                    </Button>
                    <Button id="add-backup-service-submit-button" variant="primary" type={ButtonTypes.SUBMIT} disabled={!state.selectedServiceOffering}>
                        {resources.modals.addBackupService.buttons.addBackupService.label}
                    </Button>
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};